import type { PaginationMeta } from '@/types';
import type { ResourceTableDescriptor } from './types';

/**
 * Client-side search / filter / sort / paginate, for tables that receive a plain
 * array rather than a paginated Inertia payload.
 *
 * Pure functions rather than a hook so the predicates stay testable and the
 * component below has one obvious place to compose them.
 */

export function applyClientSearch<TRow>(rows: TRow[], descriptor: ResourceTableDescriptor<TRow>, query: string): TRow[] {
    const needle = query.trim().toLowerCase();

    if (needle === '') {
        return rows;
    }

    return rows.filter((row) =>
        descriptor
            .searchValues(row)
            .some((value) => value !== null && value !== undefined && String(value).toLowerCase().includes(needle)),
    );
}

export function applyClientFilters<TRow>(
    rows: TRow[],
    descriptor: ResourceTableDescriptor<TRow>,
    columnFilters: Record<string, string[]>,
): TRow[] {
    // Iterating the descriptor's filters rather than the selection means a
    // selected value for an id with no declared predicate is a visible bug rather
    // than a silent pass-through.
    const active = descriptor.filters.filter((filter) => (columnFilters[filter.id] ?? []).length > 0);

    if (active.length === 0) {
        return rows;
    }

    return rows.filter((row) => active.every((filter) => filter.matches(row, columnFilters[filter.id])));
}

export function applyClientSort<TRow>(
    rows: TRow[],
    descriptor: ResourceTableDescriptor<TRow>,
    columnId: string | null,
    direction: 'asc' | 'desc' | null,
): TRow[] {
    if (!columnId || !direction) {
        return rows;
    }

    const accessor = descriptor.columns.find((column) => column.id === columnId)?.sort?.value;

    if (!accessor) {
        return rows;
    }

    const sign = direction === 'asc' ? 1 : -1;

    return [...rows].sort((a, b) => {
        const left = accessor(a);
        const right = accessor(b);

        // Blanks sort last in both directions: an unset value is not "smallest",
        // it is absent, and burying it keeps the populated rows together.
        if (left === null || left === undefined || left === '') {
            return right === null || right === undefined || right === '' ? 0 : 1;
        }
        if (right === null || right === undefined || right === '') {
            return -1;
        }

        if (typeof left === 'number' && typeof right === 'number') {
            return (left - right) * sign;
        }

        return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' }) * sign;
    });
}

export function paginate<TRow>(rows: TRow[], page: number, perPage: number): { items: TRow[]; meta: PaginationMeta } {
    const total = rows.length;
    const lastPage = Math.max(1, Math.ceil(total / perPage));
    const currentPage = Math.min(Math.max(1, page), lastPage);
    const start = (currentPage - 1) * perPage;
    const items = rows.slice(start, start + perPage);

    return {
        items,
        meta: {
            current_page: currentPage,
            last_page: lastPage,
            per_page: perPage,
            total,
            // Null rather than 0 when empty, matching the server's PaginationMeta —
            // the two client implementations this replaces disagreed on it.
            from: total === 0 ? null : start + 1,
            to: total === 0 ? null : start + items.length,
        },
    };
}
